import React, { useEffect, useMemo, useState } from "react";
import Slider from "react-slick";
import {
  Grid,
  Card,
  CardMedia,
  Typography,
  Box,
  ToggleButtonGroup,
  ToggleButton,
  styled,
} from "@mui/material";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const VIEW_KEY = "providerDisplay:view";

export const getSlidesToShow = (width: number, count: number): number => {
  const calculated = Math.floor((width / 1024) * 5);
  const slides = calculated > count ? count : calculated;
  return slides < 1 ? 1 : slides;
};

const StyledCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(2),
  transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[4],
  },
  cursor: "pointer",
}));

export interface Provider {
  name: string;
  image: string;
}

interface ProviderCardProps {
  provider: Provider;
  onSelectProvider: (providerName: string) => void;
}
const ProviderCard: React.FC<ProviderCardProps> = ({ provider, onSelectProvider }) => (
  <StyledCard onClick={() => onSelectProvider(provider.name)}>
    <CardMedia
      component="img"
      image={provider.image}
      alt={provider.name}
      sx={{ height: 100, objectFit: "contain", mb: 2 }}
    />
    <Typography variant="h6" textAlign="center">{provider.name}</Typography>
  </StyledCard>
);

interface ProviderListProps {
  providers: Provider[];
  onSelectProvider: (provider: string) => void;
}

export const ProviderDisplay: React.FC<ProviderListProps> = ({ providers, onSelectProvider }) => {
  // Init from localStorage (SSR-safe)
  const [view, setView] = useState<"grid" | "slider">(() => {
    if (typeof window === "undefined") return "grid";
    const saved = localStorage.getItem(VIEW_KEY);
    return saved === "slider" || saved === "grid" ? saved : "grid";
  });

  // Persist on change
  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem(VIEW_KEY, view);
  }, [view]);

  // Responsive slidesToShow
  const [slidesToShow, setSlidesToShow] = useState<number>(() =>
    typeof window === "undefined" ? 1 : getSlidesToShow(window.innerWidth, providers.length)
  );
  useEffect(() => {
    const onResize = () =>
      setSlidesToShow(getSlidesToShow(window.innerWidth, providers.length));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [providers.length]);

  const sliderSettings = useMemo(
    () => ({
      dots: true,
      infinite: providers.length > slidesToShow,
      speed: 500,
      slidesToShow,
      slidesToScroll: Math.min(2, slidesToShow),
      nextArrow: <ArrowForwardIosIcon />,
      prevArrow: <ArrowBackIosIcon />,
      responsive: [
        { breakpoint: 1536, settings: { slidesToShow: Math.min(slidesToShow, 5) } },
        { breakpoint: 1280, settings: { slidesToShow: Math.min(slidesToShow, 4) } },
        { breakpoint: 900,  settings: { slidesToShow: Math.min(slidesToShow, 3) } },
        { breakpoint: 600,  settings: { slidesToShow: Math.min(slidesToShow, 2) } },
        { breakpoint: 480,  settings: { slidesToShow: 1 } },
      ],
    }),
    [providers.length, slidesToShow]
  );

  return (
    <Box sx={{ width: "100%" }}>
      {/* View switcher */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(_, newView) => setView(newView || view)}
          aria-label="view mode"
          size="small"
        >
          <ToggleButton value="grid" aria-label="grid view"><ViewModuleIcon /></ToggleButton>
          <ToggleButton value="slider" aria-label="slider view"><ViewCarouselIcon /></ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {view === "grid" ? (
        <Grid container spacing={3}>
          {providers.map((provider) => (
            <Grid item key={provider.name} xs={6} sm={4} md={3} lg={2.4 as any}>
              <ProviderCard provider={provider} onSelectProvider={onSelectProvider} />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ ".slick-slide": { p: 1 } }}>
          <Slider {...sliderSettings}>
            {providers.map((provider) => (
              <Box key={provider.name}>
                <ProviderCard provider={provider} onSelectProvider={onSelectProvider} />
              </Box>
            ))}
          </Slider>
        </Box>
      )}
    </Box>
  );
};
